import {
  LayerStyle,
  TextLayerStyleProperties,
  LayerStyleWithSharedStyle,
  ElementType,
  SketchNonGraphicLayerElement,
  SketchGroupLayerElement,
  SketchSymbolInstanceLayerElement,
} from '../types'
import {
  SketchFrameElement,
  SketchElement,
  SketchLayerElement,
  SketchPageElement,
  SketchSectionElement,
} from '../types/SketchScene'

export function isFrameElement(
  element: SketchElement
): element is SketchFrameElement {
  return (
    element.type === ElementType.SymbolMaster ||
    element.type === ElementType.Artboard ||
    element.type === ElementType.Graphic
  )
}

export function isTopLevelFrameElement(
  element: SketchElement
): element is SketchFrameElement {
  return isFrameElement(element) && isTopLevelElement(element)
}

export function isTopLevelGroupElement(
  element: SketchElement
): element is SketchGroupLayerElement {
  return isGroupElement(element) && isTopLevelElement(element)
}

export function isTopLevelElement(element: SketchElement): boolean {
  return element.isTopLevelNode
}

/**
 * Find the closest frame element for the given selected element.
 * If there is no selected element and we are in a frame view it returns the root frame element.
 * If the selected element is a frame, it returns directly that frame.
 * If the selected element is not a frame it returns the closest ancestor frame or null if there is none.
 */
export function findClosestFrameElementForSelection(
  selectedElement: SketchElement | null,
  sketchSceneRootElement: SketchElement | null
) {
  if (!selectedElement) {
    return sketchSceneRootElement &&
      isTopLevelFrameElement(sketchSceneRootElement)
      ? sketchSceneRootElement
      : null
  }

  if (isFrameElement(selectedElement)) {
    return selectedElement
  }

  return selectedElement.frameAncestorElement ?? null
}

export function isPageElement(
  element: SketchElement
): element is SketchPageElement {
  return element.type === ElementType.Page
}

export function isSectionElement(
  element: SketchElement
): element is SketchSectionElement {
  return element.type === ElementType.Section
}

/**
 * Walk up the scene tree (including the element itself) to the nearest Section
 * ancestor, or undefined if the element is not inside a Section. When the scene
 * is rooted at a frame (FrameView) there is no Section ancestor in the tree, so
 * this naturally returns undefined there.
 */
export function getNearestSectionElement(
  element: SketchElement
): SketchSectionElement | undefined {
  let current: SketchElement | undefined = element
  while (current) {
    if (isSectionElement(current)) {
      return current
    }
    current = current.parent
  }
  return undefined
}

export function isLayerElement(
  element: SketchElement
): element is SketchLayerElement {
  return (
    !isPageElement(element) &&
    !isTopLevelFrameElement(element) &&
    !isSectionElement(element)
  )
}

export function isSymbolMasterElement(element: SketchElement) {
  return element.type === ElementType.SymbolMaster
}

export function isArtboardElementType(type: ElementType) {
  return type === ElementType.Artboard || type === ElementType.SymbolMaster
}

export function isGroupElement(
  element: SketchElement
): element is SketchGroupLayerElement | SketchSymbolInstanceLayerElement {
  return isGroupElementType(element.type)
}

export function isGroupElementType(type: ElementType) {
  return type === 'group' || type === 'symbolInstance'
}

export function isLayerWithSharedStyle(
  style: LayerStyle | undefined
): style is LayerStyleWithSharedStyle {
  return style ? 'isDirty' in style : false
}

export function isTextLayerStyle(
  style: LayerStyle
): style is TextLayerStyleProperties {
  return 'text' in style
}

export function isNonGraphicLayerElement(
  element: SketchElement
): element is SketchNonGraphicLayerElement {
  return isNonGraphicElementType(element.type)
}

export function isNonGraphicElementType(type: SketchElement['type']) {
  return type === ElementType.Hotspot || type === ElementType.Slice
}

export function getFrameWithGridAndLayout(
  element: SketchElement
): SketchFrameElement | undefined {
  if (isFrameElement(element) && (element.gridSettings || element.layerSize)) {
    return element
  }

  // If no children, no frame with grid and layout found
  if (!element.children || element.children.length === 0) {
    return undefined
  }

  // Recursively check each child until a frame with grid and layout is found
  for (const child of element.children) {
    const result = getFrameWithGridAndLayout(child)
    if (result) {
      return result
    }
  }

  return undefined
}

export function isVariantSetElement(
  element: SketchElement
): element is SketchSectionElement {
  return (
    element.type === ElementType.Section && element.variantProperties.length > 0
  )
}

/**
 * Find all the frames in the sketch scene (top & nested frames).
 */
export function getAllFrames(sketchSceneRootElement: SketchElement | null) {
  if (!sketchSceneRootElement) {
    return []
  }

  const frames: SketchFrameElement[] = []

  function findFrames(element: SketchElement) {
    if (isFrameElement(element)) {
      frames.push(element)
    }
    if (element.children) {
      element.children.forEach(findFrames)
    }
  }

  findFrames(sketchSceneRootElement)

  return frames
}
